import {
  faAddressBook,
  faBoxOpen,
  faBoxesStacked,
  faCirclePlay,
  faFileInvoiceDollar,
  faList,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AccountBalance,
  AddBusiness,
  AssistantPhoto,
  Brush,
  CloudUpload,
  Dashboard,
  Dvr,
  Handshake,
  Info,
  Insights,
  Payments,
  People,
  PointOfSale,
  Queue,
  ReceiptLong,
  ShoppingBag,
  Speed,
  Storefront,
  Toll,
  Verified,
} from '@mui/icons-material';

import { CabalAnalyticsEvent } from 'src/@types/CabalAnalyticsEvent';
import useAuth from 'src/hooks/useAuth';
import { usePermissions } from 'src/hooks/usePermissions';
import { useStrings } from 'src/hooks/useStrings';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useTaxProfileAvailable } from 'src/sections/@dashboard/TaxProfile/useTaxProfileAvailable';

interface NavModuleItem {
  title: string;
  path: string;
  icon: JSX.Element;
  shouldRender: boolean;
  isPaidModule?: boolean;
  analyticsEvent?: CabalAnalyticsEvent;
  // See NavListProps.openInNewTab.
  openInNewTab?: boolean;
  children?: {
    title: string;
    path: string;
    shouldRender: boolean;
    isPaidModule?: boolean;
    analyticsEvent?: CabalAnalyticsEvent;
  }[];
}

interface NavModule {
  subheader: string;
  items: NavModuleItem[];
}

export const useNavConfig = () => {
  const { can, is, has, isAccountType } = usePermissions();
  const { strings, currentLang } = useStrings();
  const { user } = useAuth();

  // Electronic invoicing is a legal requirement in El Salvador and the
  // integration is with a Salvadoran provider, so other countries do not see
  // it. Same rule as the "Invoice" button on the receipt.
  const isElSalvador =
    user?.current_organization?.country?.name === 'El Salvador';

  let navConfig: NavModule[] = [];

  const isTaxProfileAvailable = useTaxProfileAvailable();

  let myOrganization: NavModule = {
    subheader: strings('organizations'),
    items: [
      {
        title: strings('myOrganizations'),
        path: PATH_DASHBOARD.organizations.root,
        icon: <AddBusiness />,
        shouldRender:
          isAccountType('business') || has('multiple_organizations'),
        analyticsEvent: 'open_team',
      },
      {
        title: strings('rolesPermissions'),
        path: PATH_DASHBOARD.organizations.rolesPermissions,
        icon: <People />,
        shouldRender: isAccountType('business') && is('owner'),
        isPaidModule: true,
        analyticsEvent: 'open_employees',
      },
      {
        title: strings('taxProfileNavTitle'),
        path: PATH_DASHBOARD.organizations.taxProfile,
        icon: <AccountBalance />,
        shouldRender:
          isAccountType('business') && is('owner') && isTaxProfileAvailable,
      },
    ],
  };

  let myModules: NavModule = {
    subheader: strings('myModules'),
    items: [
      {
        title: strings('start'),
        path: PATH_DASHBOARD.POS.root,
        icon: <Dashboard />,
        shouldRender: isAccountType('business'),
      },
      {
        title: strings('tutorials'),
        path: PATH_DASHBOARD.general.tutorials,
        icon: <FontAwesomeIcon icon={faCirclePlay} />,
        shouldRender: isAccountType('business') && currentLang.value === 'es',
      },
    ],
  };

  // let cabalCard: NavModule = {
  //   subheader: 'Cabal Card',
  //   items: [
  //     {
  //       title: 'Tablero',
  //       path: PATH_DASHBOARD.cabalCard.root,
  //       icon: <Dashboard />,
  //       shouldRender: isAccountType('person'),
  //     },
  //     {
  //       title: 'Verificar cuenta',
  //       path: PATH_DASHBOARD.cabalCard.verifyAccount,
  //       icon: <Verified />,
  //       shouldRender: isAccountType('person'),
  //     },
  //   ],
  // };

  let myPOS: NavModule = {
    subheader: strings('myPOS'),
    items: [
      {
        title: strings('posSale'),
        path: PATH_DASHBOARD.POS.sales,
        icon: <PointOfSale />,
        shouldRender: isAccountType('business'),
        analyticsEvent: 'open_new_pos_sale',
      },
      {
        title: strings('orders'),
        path: PATH_DASHBOARD.POS.orders,
        icon: <Dvr />,
        shouldRender: isAccountType('business'),
        analyticsEvent: 'open_pos_orders',
      },
      {
        title: strings('products'),
        path: '',
        icon: <FontAwesomeIcon icon={faBoxOpen} />,
        shouldRender: isAccountType('business'),
        children: [
          {
            title: strings('all'),
            path: PATH_DASHBOARD.products.root,
            shouldRender:
              isAccountType('business') && can(['inventory_see_catalog']),
            analyticsEvent: 'open_inventory',
          },
          {
            title: strings('categories'),
            path: PATH_DASHBOARD.productCategories.root,
            shouldRender:
              isAccountType('business') && can(['acc_list_categories']),
          },
        ],
      },
      {
        title: strings('services'),
        path: '',
        icon: <Handshake />,
        shouldRender: isAccountType('business') && can(['service_see_catalog']),
        children: [
          {
            title: strings('all'),
            isPaidModule: true,
            path: PATH_DASHBOARD.services.root,
            shouldRender:
              isAccountType('business') && can(['service_see_catalog']),
          },
          {
            title: strings('categories'),
            isPaidModule: true,
            path: PATH_DASHBOARD.serviceCategories.root,
            shouldRender:
              isAccountType('business') && can(['acc_list_service_categories']),
          },
        ],
      },

      {
        icon: <Queue />,
        title: strings('extras'),
        isPaidModule: true,
        path: PATH_DASHBOARD.modifiers.root,
        shouldRender:
          isAccountType('business') && can(['inventory_create_products']),
        analyticsEvent: 'open_modifiers',
      },
      {
        title: strings('posAnalytics'),
        path: PATH_DASHBOARD.POS.analitycs,
        icon: <Insights />,
        shouldRender: isAccountType('business') && can(['pdv_see_analytics']),
        analyticsEvent: 'open_pos_analytics',
      },
      {
        // Free billing: issue a document that did not come from the POS. To
        // bill ONE sale there is the receipt button, which is a separate path.
        title: strings('billing'),
        path: PATH_DASHBOARD.POS.billing,
        // TuFacturaSV opens in its own tab, like the "Invoice" button of a
        // sale: the merchant keeps Cabal open where they left it.
        openInNewTab: true,
        icon: <ReceiptLong />,
        shouldRender: isAccountType('business') && isElSalvador,
      },
    ],
  };

  let myAccounting: NavModule = {
    subheader: strings('myAccounting'),
    items: [
      {
        title: strings('dashboard'),
        path: PATH_DASHBOARD.general.app,
        icon: <Speed />,
        shouldRender: is('owner'),
      },
      {
        title: strings('financialAssistant'),
        path: PATH_DASHBOARD.financialAssitant.root,
        icon: <AssistantPhoto />,
        shouldRender:
          isAccountType('business') && can(['acc_financial_statement']),
        isPaidModule: true,
        analyticsEvent: 'open_finantial_assist',
      },
      // {
      //   title: strings('financialStatements'),
      //   path: PATH_DASHBOARD.eeff.root,
      //   icon: <RequestPageIcon />,
      //   shouldRender: isAccountType('business') && is('owner'),
      //   isPaidModule: true,
      //   analyticsEvent: 'open_finantial_status',
      // },
      {
        title: strings('sales'),
        path: PATH_DASHBOARD.transactions.sale_new,
        icon: <PointOfSale />,
        shouldRender: isAccountType('business'),
        analyticsEvent: 'open_new_sale',
      },
      {
        title: strings('quotations'),
        path: PATH_DASHBOARD.quotes.index,
        icon: <FontAwesomeIcon icon={faFileInvoiceDollar} />,
        shouldRender: isAccountType('business'),
        analyticsEvent: 'open_new_sale',
        isPaidModule: true,
      },
      {
        title: strings('income'),
        path: PATH_DASHBOARD.transactions.income_new,
        icon: <PointOfSale />,
        shouldRender: isAccountType('person'),
        analyticsEvent: 'open_new_sale',
      },
      {
        title: strings('expense'),
        path: PATH_DASHBOARD.transactions.expense_new,
        icon: <Toll />,
        shouldRender: true,
        analyticsEvent: 'open_new_expense',
      },
      {
        title: strings('debts'),
        path: PATH_DASHBOARD.debts.transactions,
        icon: <Payments />,
        shouldRender: true,
        analyticsEvent: 'open_debts',
      },
      {
        title: isAccountType('person')
          ? strings('contacts')
          : strings('clients'),
        path: PATH_DASHBOARD.contacts.root,
        icon: <FontAwesomeIcon icon={faAddressBook} />,
        shouldRender: true,
      },
      {
        title: strings('transactions'),
        path: '',
        icon: <Info />,
        shouldRender: true,
        children: [
          {
            title: strings('all2'),
            path: PATH_DASHBOARD.details.root,
            shouldRender: can(['acc_see_analytics']),
          },
          {
            title: strings('categories'),
            path: PATH_DASHBOARD.transactionCategories.root,
            shouldRender: can(['acc_list_categories']),
          },
        ],
      },
    ],
  };

  let myStore: NavModule = {
    subheader: strings('myStore'),
    items: [
      {
        title: strings('myStore'),
        path: PATH_DASHBOARD.store.root,
        icon: <Storefront />,
        shouldRender: isAccountType('business'),
        analyticsEvent: 'open_online_store',
      },
      {
        title: strings('requests'),
        path: PATH_DASHBOARD.web.root(),
        icon: <ShoppingBag />,
        shouldRender: isAccountType('business'),
        analyticsEvent: 'open_web_orders',
      },
      {
        title: strings('offers'),
        path: PATH_DASHBOARD.campaigns.root,
        icon: <Verified />,
        shouldRender: isAccountType('business') && can(['web_manage_offers']),
        isPaidModule: true,
        analyticsEvent: 'open_offers',
      },
      {
        title: strings('personalization'),
        path: PATH_DASHBOARD.organizations.personalization,
        icon: <Brush />,
        shouldRender: isAccountType('business') && is('owner'),
        isPaidModule: true,
        analyticsEvent: 'open_store_customization',
      },
    ],
  };

  let myInventory: NavModule = {
    subheader: strings('myInventory'),
    items: [
      {
        title: strings('products'),
        icon: <FontAwesomeIcon icon={faBoxOpen} />,
        path: PATH_DASHBOARD.products.root,
        shouldRender:
          isAccountType('business') && can(['inventory_see_catalog']),
        analyticsEvent: 'open_inventory',
      },
      {
        title: strings('categories'),
        path: PATH_DASHBOARD.productCategories.root,
        icon: <FontAwesomeIcon icon={faList} />,
        shouldRender: isAccountType('business') && can(['acc_list_categories']),
      },
      {
        title: strings('extras'),
        path: PATH_DASHBOARD.modifiers.root,
        icon: <FontAwesomeIcon icon={faBoxesStacked} />,
        shouldRender:
          isAccountType('business') && can(['inventory_create_products']),
        isPaidModule: true,
        analyticsEvent: 'open_modifiers',
      },
      {
        title: strings('importData'),
        path: PATH_DASHBOARD.organizations.bulkUpload,
        icon: <CloudUpload />,
        shouldRender: isAccountType('business') && can(['massive_import']),
        isPaidModule: true,
        analyticsEvent: 'open_data_import',
      },
      {
        title: strings('analytics'),
        path: PATH_DASHBOARD.products.analytics,
        icon: <Insights />,
        shouldRender:
          isAccountType('business') && can(['inventory_stock_analytics']),
        isPaidModule: true,
      },
    ],
  };

  let myServices: NavModule = {
    subheader: strings('services'),
    items: [
      {
        title: strings('all'),
        icon: <Handshake />,
        path: PATH_DASHBOARD.services.root,
        shouldRender:
          isAccountType('business') && can(['inventory_see_catalog']),
        isPaidModule: true,
      },
      {
        title: strings('categories'),
        path: PATH_DASHBOARD.serviceCategories.root,
        icon: <FontAwesomeIcon icon={faList} />,
        shouldRender: isAccountType('business') && can(['acc_list_categories']),
        isPaidModule: true,
      },
    ],
  };

  navConfig = [myAccounting];

  if (isAccountType('person')) {
    // navConfig.splice(0, 0, cabalCard);
  }

  if (isAccountType('business')) {
    navConfig.splice(0, 0, myModules);
    navConfig.splice(1, 0, myPOS);
    navConfig.push(myStore);
    navConfig.push(myInventory);
    navConfig.push(myServices);
  }

  if (isAccountType('business') || has('multiple_organizations')) {
    navConfig.splice(2, 0, myOrganization);
  }

  return navConfig;
};
